import React, {createRef, useContext, useEffect, useRef, useState} from "react";
import {useQueryClient} from "react-query";
import { useAuth0 } from "@auth0/auth0-react";
import "../../App.css";
import __ from "lodash";

import { makeStyles } from "@material-ui/core/styles";
import { Toolbar, Tooltip } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import { FastForward, FastRewind } from "@material-ui/icons";

import PageHeader from "../../blocks/PageHeader.js";
import PageFooter from "../../blocks/PageFooter";
import ChromeTabs from "../../controls/ChromeTabs";
import { getDateTabs } from "../../functions/DateTabs";
import SlotManagementStyles from "./styles/slotManagementStyles";

import SlotList from "./components/SlotList";
import FlightsMissingSlots2 from "./components/FlightsMissingSlots2";
import SlotReviewList from "./components/SlotLReviewList";
import { useAddUserSetting, useSeasons, useUpdateUserSetting, useUserSettings } from "../../services/settings";
import {useCountSlots} from "../../services/slots";
import SlotListExpanded from "./components/SlotListExpanded";
import SystemContext from "../../SystemContext";

const AccessControl = require("../../AccessControl");


function SlotManagement(props) {
  const classes = makeStyles(SlotManagementStyles)();

  const SystemData = useContext(SystemContext);
  const eventBus = SystemData.eventBus;

  const { isAuthenticated, user } = useAuth0();
  const canEdit = AccessControl.userHasAccess(user, [
    "Super Admin",
    "Admin",
    "Manage SLOTS",
    "Admin SLOTS",
  ]);
  const canManuallyUpdateSlots = AccessControl.userHasAccess(user, [
    "Super Admin", "Admin", "Admin SLOTS",
  ]);

  const queryClient = useQueryClient();

  const flags = useRef({
    settingsDone: false,
    loadedSetting: false,
  });

  const [state, setState] = useState({
    monthTabIndex: canEdit ? "outstanding" : 3,
    missingSlotsCount2: 0,
    reviewSlotsCount: 0,
    showExplodedView: false,
  });

  const userSetting = useUserSettings({queryClient, extraKeys: ["SLOT MANAGEMENT", "LAST VIEW STATE"], callback:(data)=> {
    flags.current.loadedSetting = true; 
  }});
  const handleAddUserSetting = useAddUserSetting();
  const handleUpdateUserSetting = useUpdateUserSetting();

  if (userSetting.data && Array.isArray(userSetting.data)) {
    if (userSetting.data.length <= 0 && !flags.current.settingsDone) {
      flags.current.settingsDone = true;
      handleAddUserSetting.mutate({extraKeys:['SLOT MANAGEMENT', 'LAST VIEW STATE'], record: {
        module: 'SLOT MANAGEMENT', 
        area: 'LAST VIEW STATE', 
        values: {
          showExplodedView: false,
          hideCancelled: false
        }
      }});
    } else if (!flags.current.settingsDone) {
      flags.current.settingsDone = true;
    }
  }

  const [listTabs, setListTabs] = useState([
    { label: "Slots Requiring Review", value: "review", badge: {content: state.reviewSlotsCount} },
    { label: "Flights Missing Slots", value: "missing", badge: {content: state.missingOrderCount2} },
    { label: "Outstanding", value: "outstanding" },
  ]);
  const [monthTabs, setMonthTabs] = useState(
    getDateTabs(undefined, undefined, 3, 2)
  );

  useEffect(()=> {
    const handleToggleExplodedView = () => {
      if (userSetting.data && Array.isArray(userSetting.data) && userSetting.data.length > 0) {
        const showExplodedView = !userSetting.data[0].values?.showExplodedView;
        const oldRecord = userSetting.data[0];
        const oldValues = oldRecord.values;
        const newValues = {...oldValues, showExplodedView};
        handleUpdateUserSetting.mutate({extraKeys:['SLOT MANAGEMENT', 'LAST VIEW STATE'], record: {...oldRecord, values: newValues}, oldRecord});
      }
    }
    const handleToggleCancelled = () => {
      if (userSetting.data && Array.isArray(userSetting.data) && userSetting.data.length > 0) {
        const hideCancelled = !userSetting.data[0].values?.hideCancelled;
        const oldRecord = userSetting.data[0];
        const oldValues = oldRecord.values;
        const newValues = {...oldValues, hideCancelled};
        handleUpdateUserSetting.mutate({extraKeys:['SLOT MANAGEMENT', 'LAST VIEW STATE'], record: {...oldRecord, values: newValues}, oldRecord});
      }
    }
  
    eventBus.bind(`SlotManagement.toggleExplodedView`, handleToggleExplodedView);
    eventBus.bind(`SlotManagement.toggleCancelled`, handleToggleCancelled);

    return ()=> {
      eventBus.unbind(`SlotManagement.toggleExplodedView`, handleToggleExplodedView);
      eventBus.unbind(`SlotManagement.toggleCancelled`, handleToggleCancelled);
    }
  },[eventBus, handleUpdateUserSetting, userSetting.data]);

  const handleChangeMonth = (e, i) => {
    const change = { monthTabIndex: i };
    handleChange(change);
  };

  const handleChange = (change) => {
    const { month, year } =
      state.monthTabIndex === "review" ||
      state.monthTabIndex === "outstanding" ||
      state.monthTabIndex === "missing" 
        ? { month: 0, year: 0 }
        : monthTabs.values[change.monthTabIndex];
    const newState = { ...change, month, year };
    if (newState) {
      setState({
        ...state,
        ...newState,
      });
    }
  };

  const handleTabSetChange = (index) => {
    setMonthTabs(getDateTabs(monthTabs, index, 5, 0));
  };

  const handleTabChange = (e, tab) => {
    setState({ monthTabIndex: tab });
  };

  const currentTab = (state.monthTabIndex = state.monthTabIndex || 0);
  const { month, year } =
    state.monthTabIndex === "review" ||
    state.monthTabIndex === "outstanding" ||
    state.monthTabIndex === "missing" 
      ? { month: 0, year: 0 }
      : monthTabs.values[state.monthTabIndex];

  const extraKeys = [];
  const seasons = useSeasons({queryClient, extraKeys, useCache: true});

  const missingExtraKeys2 = [0, 0, "missing"];
  const cancelledExtraKeys = [0, 0, "review"];

  let reviewSlotsCount = useCountSlots({queryClient, extraKeys: cancelledExtraKeys});
  let missingSlotsCount2 = useCountSlots({queryClient, extraKeys: missingExtraKeys2});

  useEffect(()=> {
    if (missingSlotsCount2 && missingSlotsCount2.status === 'success' && missingSlotsCount2.data !== undefined && missingSlotsCount2.data !== state.missingOrderCount2) {
      setState({...state, missingSlotsCount2: missingSlotsCount2.data});
      const tab = __.find(listTabs, {value: "missing"});
      if (tab)
        tab.badge = {content: missingSlotsCount2.data};
    }
    if (reviewSlotsCount && reviewSlotsCount.status === 'success' && reviewSlotsCount.data !== undefined && reviewSlotsCount.data !== state.cancelOrderCount) {
      setState({...state, reviewSlotsCount: reviewSlotsCount.data});
      const tab = __.find(listTabs, {value: "review"});
      if (tab)
        tab.badge = {content: reviewSlotsCount.data};
    }
  },[reviewSlotsCount.data, missingSlotsCount2.data]);

  return (
    isAuthenticated && (
      <div
        className="SlotManagement"
        style={{
          backgroundColor: "#eeeeee",
          color: "#1b4481",
          fontWeight: 400,
          fontFamily: "Ubuntu,sans-serif",
          minHeight: "100vh",
        }}
      >
        <PageHeader
          parentProps={props}
          activeMenuItem="slotmanagement"
          pageName="Slot Management"
          loggedInUser={user}
          logout={""}
        ></PageHeader>
        <div
          style={{
            marginLeft: 40,
            marginRight: 40,
            height: 2,
            backgroundColor: "#b7c3d4",
          }}
        ></div>
        <div style={{ height: 10 }}></div>

        <div
          className={classes.rooted}
          style={{ marginLeft: 20, marginRight: 20 }}
        >
          {canEdit ? (
            <Toolbar>
              <ChromeTabs
                id={"Annexes"}
                style={{ alignSelf: "flex-end" }}
                tabs={listTabs}
                tabStyle={{
                  bgColor: "#E6E8EB",
                  selectedBgColor: "#ffffff",
                  color: "rgba(0,0,0,0.87)",
                }}
                tabProps={{
                  disableRipple: false,
                }}
                value={currentTab}
                onChange={handleTabChange}
              />
            </Toolbar>
          ) : (
            <></>
          )}
          <Toolbar>
            <div
              style={{
                width: "50px",
                marginRight: 10,
                marginLeft: 10,
                height: 45,
              }}
            >
              <IconButton
                color="primary"
                onClick={() => {
                  handleTabSetChange(-1);
                }}
              >
                <FastRewind fontSize="large" />
              </IconButton>
            </div>
            <ChromeTabs
              id={"Months"}
              style={{ alignSelf: "flex-end" }}
              tabs={monthTabs.tabs}
              tabStyle={{
                bgColor: "#E6E8EB",
                selectedBgColor: "#ffffff",
                color: "rgba(0,0,0,0.87)",
              }}
              tabProps={{
                disableRipple: false,
              }}
              value={currentTab}
              onChange={handleChangeMonth}
            />
            <div
              style={{
                width: "50px",
                marginRight: 10,
                marginLeft: 10,
                height: 45,
              }}
            >
              <IconButton
                color="primary"
                onClick={() => {
                  handleTabSetChange(1);
                }}
              >
                <FastForward fontSize="large" />
              </IconButton>
            </div>
          </Toolbar>
        </div>
        <div
          className={classes.rooted}
          style={{
            marginLeft: 20,
            marginRight: 20,
            minHeight: `calc(100vh - ${99 + 2 + 10 + 57 + 64 + 4}px)`,
          }}
        >
          {currentTab === "review" ? (
              <SlotReviewList
                classes={classes}
                canEdit={canEdit}
                month={month}
                year={year}
                required={"review"}
              />
          ) : currentTab === "missing" ? (
            <FlightsMissingSlots2
              classes={classes}
              canEdit={canEdit}
              month={month}
              year={year}
              required={'missing'}
            />  
            ) : currentTab === "outstanding" ? (
            <SlotList
              classes={classes}
              canEdit={canEdit}
              month={month}
              year={year}
              seasons={seasons}
              canManuallyUpdateSlots={canManuallyUpdateSlots}
              eventBus={eventBus}
              canToggleExplodedView={false}
            />
          ) : userSetting && Array.isArray(userSetting.data) && userSetting.data.length > 0 &&  userSetting.data[0].values?.showExplodedView ? (
            <SlotListExpanded
              classes={classes}
              canEdit={canEdit}
              month={month}
              year={year}
              seasons={seasons}
              canManuallyUpdateSlots={canManuallyUpdateSlots}
              eventBus={eventBus}
              canToggleExplodedView={true}
              hideCancelled={userSetting.data[0].values.hideCancelled}
            />
          ) : (
            <SlotList
              classes={classes}
              canEdit={canEdit}
              month={month}
              year={year}
              canManuallyUpdateSlots={canManuallyUpdateSlots}
              eventBus={eventBus}
              canToggleExplodedView={true}
            />
          )}
        </div>

        <PageFooter></PageFooter>
      </div>
    )
  );
}

export default SlotManagement;
